import { AmountInModifier, Service } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { Modal, Button, TextInput, NumberInput, SegmentedControl, ActionIcon, Badge } from '@mantine/core';
import { IconPlus, IconTrash, IconAdjustmentsHorizontal, IconEdit } from '@tabler/icons-react';
import { formatPrice } from '@/lib/format-utils';
import { MoneyNumberInput } from './snippets';

interface Props {
    service: Service;
    modifiers: AmountInModifier[];
    onChange: (modifiers: AmountInModifier[]) => Promise<void> | void;
    isSaving?: boolean;
    title?: string;
    type?: 'price' | 'cost';
}

export default function EditModifiersModal({ service, modifiers = [], onChange, isSaving = false, title, type = 'price' }: Props) {
    const { t } = useTranslation();
    const displayTitle = title || (type === 'cost' ? t('service_cost_modifiers') : t('service_price_modifiers'));
    const [opened, setOpened] = useState(false);
    const [editingMods, setEditingMods] = useState<AmountInModifier[]>([]);
    const [newName, setNewName] = useState('');
    const [newType, setNewType] = useState<'add' | 'subtract'>('add');
    const [newAmount, setNewAmount] = useState<number | undefined>();

    const hasChanges = useMemo(() => {
        return JSON.stringify(modifiers) !== JSON.stringify(editingMods);
    }, [modifiers, editingMods]);

    const handleOpen = () => {
        setEditingMods([...(modifiers || [])]);
        setOpened(true);
    };

    const handleClose = () => {
        setOpened(false);
        setNewName('');
        setNewType('add');
        setNewAmount(undefined);
        setEditingMods([...(modifiers || [])]);
    };

    const handleSave = async () => {
        await onChange(editingMods);
        setOpened(false);
    };

    const handleAdd = () => {
        const trimmed = newName.trim();
        if (!trimmed || newAmount === undefined || isNaN(newAmount) || newAmount <= 0) {
            return;
        }

        const signedAmount = newType === 'subtract' ? -Math.abs(newAmount) : Math.abs(newAmount);
        const newMod: AmountInModifier = {
            id: `mod_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            name: trimmed,
            amount: signedAmount,
        };

        setEditingMods([...editingMods, newMod]);
        setNewName('');
        setNewAmount(undefined);
    };

    const handleRemove = (index: number) => {
        setEditingMods(editingMods.filter((_, i) => i !== index));
    };

    const defaultCurrency = type === 'cost'
        ? (service.sell_costs?.[0]?.default_currency || service.sell_prices?.[0]?.default_currency || '**')
        : (service.sell_prices?.[0]?.default_currency || '**');

    return (
        <>
            <div className="solid-box-border w-full md:w-auto">
                <div className="flex gap-4 items-center justify-between mb-4">
                    <div className="text-md font-bold">
                        {displayTitle}
                    </div>
                    <Button
                        size="md"
                        color="black"
                        variant="subtle"
                        onClick={handleOpen}
                        loading={isSaving}
                    >
                        <IconEdit size={20} />
                    </Button>
                </div>
                <div className="text-sm space-y-1">
                    {modifiers && modifiers.length > 0 ? (
                        modifiers.map((mod, index) => {
                            const isAddition = mod.amount >= 0;
                            return (
                                <div
                                    key={mod.id || `display-${index}`}
                                    className="flex gap-4 justify-between items-center solid-box-border-tinted"
                                >
                                    <div className="font-bold text-gray-700 truncate">
                                        {mod.name}
                                    </div>
                                    <div className={`font-bold shrink-0 ${isAddition ? 'text-green-700' : 'text-red-700'}`}>
                                        {isAddition ? '+' : ''}{formatPrice(mod.amount)} {defaultCurrency}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div
                            onClick={handleOpen}
                            className="text-xs text-gray-400 italic py-2 text-center cursor-pointer hover:text-gray-600 solid-box-border-tinted"
                        >
                            {t('no_modifiers_configured')}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                opened={opened}
                onClose={handleClose}
                title={(
                    <div className="flex items-center gap-2">
                        <IconAdjustmentsHorizontal size={20} className="text-gray-700" />
                        <span className="font-bold text-gray-900">
                            {displayTitle}
                        </span>
                    </div>
                )}
                size="lg"
                radius="md"
            >
                <div className="space-y-4">
                    {/* Add new modifier form */}
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            {t('add_service_modifier')}
                        </div>
                        <div className="flex flex-row flex-wrap gap-2 items-end">
                            <div className="flex-1 min-w-[200px]">
                                <TextInput
                                    placeholder={t('modifier_name')}
                                    size="sm"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAdd();
                                        }
                                    }}
                                />
                            </div>
                            <div className="w-30">
                                <SegmentedControl
                                    size="xs"
                                    value={newType}
                                    onChange={(val: any) => setNewType(val)}
                                    data={[
                                        { label: '+', value: 'add' },
                                        { label: '-', value: 'subtract' }
                                    ]}
                                    fullWidth
                                />
                            </div>
                            <div className="flex-1">
                                <MoneyNumberInput
                                    amount={newAmount}
                                    onChange={(val) => setNewAmount(val)}
                                    rightSection={(
                                        <div className='text-xxs'>{defaultCurrency}</div>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button
                                size="xs"
                                color="black"
                                radius="md"
                                onClick={handleAdd}
                                disabled={!newName.trim() || newAmount === undefined || newAmount <= 0}
                                leftSection={<IconPlus size={14} />}
                            >
                                {t('add')}
                            </Button>
                        </div>
                    </div>

                    {/* Modifiers list */}
                    <div className="space-y-2">
                        {editingMods.length === 0 ? (
                            <div className="text-center py-6 text-sm text-gray-400">
                                {t('no_modifiers_configured')}
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                {editingMods.map((mod, idx) => {
                                    const isAddition = mod.amount >= 0;
                                    return (
                                        <div
                                            key={mod.id || idx}
                                            className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Badge
                                                    size="sm"
                                                    color={isAddition ? 'green' : 'red'}
                                                    variant="light"
                                                    className="shrink-0"
                                                >
                                                    {isAddition ? '+' : '-'}
                                                </Badge>
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {mod.name}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className={`text-sm font-bold ${isAddition ? 'text-green-700' : 'text-red-700'}`}>
                                                    {isAddition ? '+' : ''}{formatPrice(mod.amount)} {defaultCurrency}
                                                </span>
                                                <ActionIcon
                                                    color="red"
                                                    variant="subtle"
                                                    size="sm"
                                                    onClick={() => handleRemove(idx)}
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                        <Button
                            variant="default"
                            size="xs"
                            radius="md"
                            onClick={handleClose}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="md"
                            onClick={handleSave}
                            loading={isSaving}
                            disabled={!hasChanges}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
